import { Controller } from "@hotwired/stimulus"

/**
 * BikeHunterTabsController
 * 
 * Handles instant tab switching between Cronograma and Ruta views
 * with lazy-loaded Google Maps integration and data caching.
 */
export default class extends Controller {
  static targets = ["tab", "cronogramaPanel", "rutaPanel", "advertenciasPanel", "mapContainer", "loadingIndicator", "routeInfo", "warningsList", "warningCountBadge"]
  static values = {
    date: String,
    mapLoaded: { type: Boolean, default: false },
    activeTab: { type: String, default: "cronograma" }
  }

  connect() {
    console.log("🚴 BikeHunterTabs: Connected for date", this.dateValue)
    this.cachedRouteData = null
    this.map = null
    this.markers = []
    this.routePath = null
    this.pendingTicketId = null // Track ticket to return to from resolution
    this.currentInfoWindow = null // Track currently open InfoWindow

    // Listen for address updates to refresh route
    this.handleAddressUpdate = this.handleAddressUpdate.bind(this)
    window.addEventListener('address-updated', this.handleAddressUpdate)

    // Listen for edit ticket address events
    this.handleEditTicketAddress = this.handleEditTicketAddress.bind(this)
    window.addEventListener('edit-ticket-address', this.handleEditTicketAddress)

    // Listen for view/resolve ticket from map events
    this.handleViewTicketFromMap = this.handleViewTicketFromMap.bind(this)
    this.handleResolveTicketFromMap = this.handleResolveTicketFromMap.bind(this)
    window.addEventListener('view-ticket-from-map', this.handleViewTicketFromMap)
    window.addEventListener('resolve-ticket-from-map', this.handleResolveTicketFromMap)

    // Listen for resolution complete/cancel to return to map
    this.handleResolutionComplete = this.handleResolutionComplete.bind(this)
    window.addEventListener('ticket-resolution-complete', this.handleResolutionComplete)
    window.addEventListener('ticket-resolution-cancel', this.handleResolutionComplete)

    // Restore tab from sessionStorage if available
    const savedTab = sessionStorage.getItem(`bikeHunterTab_${this.dateValue}`)
    if (savedTab && savedTab !== this.activeTabValue) {
      this.activeTabValue = savedTab
      this.updateTabStyles()
      this.updatePanelVisibility()

      if (savedTab === "ruta") {
        this.loadMap()
      }
    }

    // Always check for warnings in background
    this.checkForWarnings()
  }

  disconnect() {
    console.log("🚴 BikeHunterTabs: Disconnected")
    window.removeEventListener('address-updated', this.handleAddressUpdate)
    window.removeEventListener('edit-ticket-address', this.handleEditTicketAddress)
    window.removeEventListener('view-ticket-from-map', this.handleViewTicketFromMap)
    window.removeEventListener('resolve-ticket-from-map', this.handleResolveTicketFromMap)
    window.removeEventListener('ticket-resolution-complete', this.handleResolutionComplete)
    window.removeEventListener('ticket-resolution-cancel', this.handleResolutionComplete)
    this.markers = []
    this.map = null
    this.routePath = null
  }

  /**
   * Handle address update events - refresh route
   */
  handleAddressUpdate(event) {
    console.log("🚴 BikeHunterTabs: Address updated, refreshing route...", event.detail)
    this.refreshRouteData()
    // Always check for warnings after an update, regardless of current tab
    this.checkForWarnings()
  }

  /**
   * Handle edit ticket address/field request
   */
  handleEditTicketAddress(event) {
    const ticketId = event.detail.ticketId
    const field = event.detail.field || 'direccion_reparacion'
    console.log(`🚴 BikeHunterTabs: Edit ticket field ${field}:`, ticketId)

    this.switchToCronogramaAndZoomTicket(ticketId, { focusField: field })
  }

  /**
   * Handle view ticket from map - switch to Cronograma and zoom ticket
   */
  handleViewTicketFromMap(event) {
    const ticketId = event.detail.ticketId
    console.log("🚴 BikeHunterTabs: View ticket from map:", ticketId)

    this.switchToCronogramaAndZoomTicket(ticketId)
  }

  /**
   * Handle resolve ticket from map - switch to Cronograma, zoom ticket and open resolution form
   */
  handleResolveTicketFromMap(event) {
    const ticketId = event.detail.ticketId
    console.log("🚴 BikeHunterTabs: Resolve ticket from map:", ticketId)

    // Mark that user came from map - to return after resolution
    this.cameFromMap = true
    this.pendingTicketId = ticketId

    this.switchToCronogramaAndZoomTicket(ticketId, { openResolution: true })
  }

  /**
   * Handle resolution complete/cancel - return to map only if user came from map
   */
  handleResolutionComplete(event) {
    console.log("🚴 BikeHunterTabs: Resolution complete/cancel")

    // Only return to map if user came from there
    if (!this.cameFromMap) {
      console.log("🚴 BikeHunterTabs: User didn't come from map, staying in Cronograma")
      return
    }

    console.log("🚴 BikeHunterTabs: Returning to map")

    // Clear flags
    this.cameFromMap = false
    this.pendingTicketId = null

    // Switch back to Ruta tab
    this.activeTabValue = "ruta"
    sessionStorage.setItem(`bikeHunterTab_${this.dateValue}`, "ruta")
    this.updateTabStyles()
    this.updatePanelVisibility()

    // Refresh route data in case ticket was resolved
    this.refreshRouteData()
  }

  /**
   * Switch to Cronograma tab and zoom a specific ticket
   */
  switchToCronogramaAndZoomTicket(ticketId, options = {}) {
    const { openResolution = false, focusField = null } = options

    // Switch to Cronograma
    this.activeTabValue = "cronograma"
    sessionStorage.setItem(`bikeHunterTab_${this.dateValue}`, "cronograma")
    this.updateTabStyles()
    this.updatePanelVisibility()

    // Find and zoom the ticket
    setTimeout(() => {
      const ticketElement = document.getElementById(`ticket_${ticketId}`)
      if (ticketElement) {
        // Scroll to ticket
        ticketElement.scrollIntoView({ behavior: 'smooth', block: 'center' })

        // Get the card-zoom controller and toggle zoom
        const cardZoomController = this.application.getControllerForElementAndIdentifier(ticketElement, 'card-zoom')
        if (cardZoomController) {
          // Ensure it's zoomed - simulate a click event with the correct currentTarget
          if (!ticketElement.classList.contains('zoomed')) {
            // Create a mock event that looks like a real click event
            const mockEvent = {
              currentTarget: ticketElement,
              target: ticketElement,
              stopPropagation: () => { },
              preventDefault: () => { }
            }
            cardZoomController.toggle(mockEvent)
          }

          // If opening resolution, click the resolve button after zoom animation
          if (openResolution) {
            setTimeout(() => {
              const resolveBtn = ticketElement.querySelector('[data-action="click->ticket-resolution#toggle"]')
              if (resolveBtn) {
                resolveBtn.click()
              }
            }, 400)
          }

          // If focusing a field
          if (focusField) {
            setTimeout(() => {
              const input = ticketElement.querySelector(`[data-field="${focusField}"]`)
              if (input) {
                input.focus()
                // Highlight the input temporarily
                input.classList.add('ring-2', 'ring-blue-500', 'bg-blue-50')
                setTimeout(() => {
                  input.classList.remove('ring-2', 'ring-blue-500', 'bg-blue-50')
                }, 2000)
              }
            }, 400)
          }
        }
      }
    }, 100)
  }

  /**
   * Switch to a tab
   * @param {Event} event - Click event from tab button
   */
  switchTab(event) {
    event.preventDefault()
    const tab = event.currentTarget.dataset.tab

    if (tab === this.activeTabValue) return

    console.log(`🚴 BikeHunterTabs: Switching to ${tab}`)
    this.activeTabValue = tab

    // Save to sessionStorage
    sessionStorage.setItem(`bikeHunterTab_${this.dateValue}`, tab)

    this.updateTabStyles()
    this.updatePanelVisibility()

    // Lazy load map when Ruta or Advertencias tab is first selected (we need route data for warnings too)
    if ((tab === "ruta" || tab === "advertencias") && !this.mapLoadedValue) {
      this.loadMap()
    }
  }

  /**
   * Update tab button styles based on active tab
   */
  updateTabStyles() {
    this.tabTargets.forEach(tabEl => {
      const isActive = tabEl.dataset.tab === this.activeTabValue

      // Remove all state classes first
      tabEl.classList.remove(
        "bg-blue-50", "text-blue-800", "ring-1", "ring-blue-400",
        "text-gray-700", "hover:bg-gray-50"
      )

      // Add appropriate classes
      if (isActive) {
        tabEl.classList.add("bg-blue-50", "text-blue-800", "ring-1", "ring-blue-400")
      } else {
        tabEl.classList.add("text-gray-700", "hover:bg-gray-50")
      }
    })
  }

  /**
   * Toggle panel visibility
   */
  updatePanelVisibility() {
    if (this.hasCronogramaPanelTarget) {
      this.cronogramaPanelTarget.classList.toggle("hidden", this.activeTabValue !== "cronograma")
    }
    if (this.hasRutaPanelTarget) {
      this.rutaPanelTarget.classList.toggle("hidden", this.activeTabValue !== "ruta")
    }
    if (this.hasAdvertenciasPanelTarget) {
      this.advertenciasPanelTarget.classList.toggle("hidden", this.activeTabValue !== "advertencias")
    }
  }

  /**
   * Load Google Maps and route data
   */
  async loadMap() {
    try {
      console.log("🗺️ BikeHunterTabs: Loading map...")

      // Show loading indicator
      if (this.hasLoadingIndicatorTarget) {
        this.loadingIndicatorTarget.classList.remove("hidden")
      }
      if (this.hasMapContainerTarget) {
        this.mapContainerTarget.classList.add("hidden")
      }

      // Load Google Maps API if not present
      await this.ensureGoogleMapsLoaded()

      // Fetch route data (use cache if available)
      const routeData = await this.fetchRouteData()

      // Always update warnings panel
      this.updateWarningsPanel(routeData)

      if (routeData.success) {
        // Initialize map
        this.initializeMap(routeData)
        this.mapLoadedValue = true
        console.log("🗺️ BikeHunterTabs: Map loaded successfully")
      } else {
        // Show error but we already updated warnings
        throw new Error(routeData.error || 'Error loading route')
      }

    } catch (error) {
      console.error("❌ BikeHunterTabs: Error loading map:", error)
      this.showMapError(error.message)
    }
  }

  /**
   * Ensure Google Maps API is loaded
   */
  async ensureGoogleMapsLoaded() {
    if (window.google && window.google.maps) {
      return Promise.resolve()
    }

    const apiKey = document.querySelector('meta[name="google-maps-api-key"]')?.content
    if (!apiKey) {
      throw new Error('Google Maps API key not found')
    }

    return new Promise((resolve, reject) => {
      // Set up error callback for Google Maps auth errors
      window.gm_authFailure = () => {
        console.error('🗺️ Google Maps auth failure - check API key restrictions in Google Cloud Console')
        reject(new Error('Google Maps authentication failed. The API key may have referrer restrictions that block PWA access.'))
      }

      const script = document.createElement('script')
      // Added 'marker' library for AdvancedMarkerElement support
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry,marker&callback=Function.prototype`
      script.async = true
      script.defer = true
      script.onload = () => {
        // Small delay to allow Google Maps to initialize
        setTimeout(resolve, 100)
      }
      script.onerror = () => reject(new Error('Failed to load Google Maps script'))
      document.head.appendChild(script)
    })
  }

  /**
   * Fetch route data from server (with caching)
   */
  async fetchRouteData() {
    // Return cached data if available
    if (this.cachedRouteData) {
      console.log("🗺️ BikeHunterTabs: Using cached route data")
      return this.cachedRouteData
    }

    // Deduplicate in-flight requests
    if (this.fetchPromise) {
      console.log("🗺️ BikeHunterTabs: Waiting for in-flight route fetch...")
      return this.fetchPromise
    }

    this.fetchPromise = (async () => {
      try {
        const response = await fetch(`/apps/home-repairs/tickets/daily_route_data?date=${this.dateValue}`, {
          credentials: 'same-origin',
          headers: {
            'Accept': 'application/json'
          }
        })

        if (!response.ok) {
          // Do not throw on HTTP errors, return a structured error response
          // This allows the caller to handle the error gracefully, e.g., show warnings
          const errorData = await response.json().catch(() => ({ error: `HTTP error: ${response.status}` }))
          return { success: false, error: errorData.error || `HTTP error: ${response.status}`, warnings: errorData.warnings || [] }
        }

        const data = await response.json()

        // Cache the data
        this.cachedRouteData = data
        return data
      } finally {
        this.fetchPromise = null
      }
    })()

    return this.fetchPromise
  }

  /**
   * Check for warnings without initializing the map
   */
  async checkForWarnings() {
    try {
      // Fetch route data (will be cached for when map is needed)
      // This populates the warnings/error badge
      const data = await this.fetchRouteData()

      this.updateWarningsPanel(data)

    } catch (error) {
      console.warn("⚠️ BikeHunterTabs: Could not check for warnings:", error)
    }
  }

  /**
   * Initialize Google Map with route data
   */
  initializeMap(data) {
    if (!this.hasMapContainerTarget) {
      throw new Error('Map container not found')
    }

    // Hide loading, show map
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add("hidden")
    }
    this.mapContainerTarget.classList.remove("hidden")

    // Get Map ID from meta tag (optional, but required for AdvancedMarkerElement)
    const mapId = document.querySelector('meta[name="google-maps-map-id"]')?.content

    // Create map with mapId for AdvancedMarkerElement support
    this.map = new google.maps.Map(this.mapContainerTarget, {
      zoom: 13,
      center: data.origin,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
      gestureHandling: 'greedy', // Better touch handling on mobile
      mapId: mapId || 'DEMO_MAP_ID' // DEMO_MAP_ID works for basic AdvancedMarkerElement
    })

    // Close InfoWindow when clicking on the map (outside markers)
    this.map.addListener('click', () => {
      if (this.currentInfoWindow) {
        this.currentInfoWindow.close()
        this.currentInfoWindow = null
      }
    })

    // Add origin marker (Sede)
    this.addOriginMarker(data.origin)

    // Add ticket markers
    data.tickets.forEach((ticket, index) => {
      this.addTicketMarker(ticket, index)
    })

    // Draw route polyline
    if (data.polyline) {
      this.drawRoute(data)
    }

    // Fit bounds to show entire route if there are tickets, otherwise just origin
    if (data.tickets.length > 0) {
      const bounds = new google.maps.LatLngBounds()
      bounds.extend(data.origin)
      data.tickets.forEach(ticket => bounds.extend(ticket.coordinates))
      this.map.fitBounds(bounds)
    } else {
      this.map.setCenter(data.origin)
      this.map.setZoom(13)
    }

    // Show route info
    this.showRouteInfo(data)
  }

  /**
   * Add origin marker to map (using AdvancedMarkerElement)
   */
  addOriginMarker(origin) {
    const { AdvancedMarkerElement } = google.maps.marker

    // Create custom HTML element for origin marker
    const markerContent = document.createElement('div')
    markerContent.className = 'origin-marker'
    markerContent.innerHTML = `
      <div style="
        background: #1F2937;
        color: white;
        padding: 3px 6px;
        border-radius: 5px;
        box-shadow: 0 1px 4px rgba(0,0,0,0.3);
        font-family: 'Montserrat', sans-serif;
        font-size: 8px;
        font-weight: 700;
        white-space: nowrap;
        border: 1px solid white;
        letter-spacing: 0.5px;
      ">PANOT</div>
      <div style="
        width: 0;
        height: 0;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        border-top: 4px solid #1F2937;
        margin: 0 auto;
      "></div>
    `

    const marker = new AdvancedMarkerElement({
      position: origin,
      map: this.map,
      title: 'Panot',
      content: markerContent
    })

    this.markers.push(marker)
  }

  /**
   * Add ticket marker to map (using AdvancedMarkerElement with mini-ticket card design)
   */
  addTicketMarker(ticket, index) {
    const { AdvancedMarkerElement } = google.maps.marker

    // Determine colors based on warnings
    const hasWarning = ticket.warning
    const borderColor = hasWarning ? '#EF4444' : '#E5E7EB'
    const bgColor = hasWarning ? '#FEF2F2' : '#FFFFFF'
    const badgeColor = hasWarning ? '#EF4444' : '#F97316'

    // Pipeline color
    const pipelineColors = {
      'programado': '#3B82F6',      // Blue
      'en_reparacion': '#F97316',   // Orange
      'pendiente_respuesta': '#8B5CF6', // Purple
      'resuelto': '#10B981',        // Green
      'default': '#6B7280'          // Gray
    }
    const pipelineColor = pipelineColors[ticket.pipeline_stage] || pipelineColors['default']
    const pipelineName = this.formatPipelineName(ticket.pipeline_stage || 'pendiente')

    // Truncate subject for mini card (shorter for compact size)
    const truncatedSubject = ticket.subject && ticket.subject.length > 12
      ? ticket.subject.substring(0, 12) + '...'
      : (ticket.subject || 'Sin asunto')

    // Create custom HTML element for ticket marker (mini-card style)
    const markerContent = document.createElement('div')
    markerContent.className = 'ticket-marker'
    markerContent.innerHTML = `
      <div style="
        background: ${bgColor};
        border: 1px solid ${borderColor};
        border-radius: 4px;
        box-shadow: 0 1px 4px rgba(0,0,0,0.25);
        font-family: 'Montserrat', sans-serif;
        min-width: 45px;
        max-width: 65px;
        overflow: hidden;
        cursor: pointer;
        transition: transform 0.15s ease, box-shadow 0.15s ease;
      " onmouseover="this.style.transform='scale(1.1)'; this.style.boxShadow='0 2px 8px rgba(0,0,0,0.35)';"
         onmouseout="this.style.transform='scale(1)'; this.style.boxShadow='0 1px 4px rgba(0,0,0,0.25)';">
        
        <!-- Header with number and warning -->
        <div style="
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 2px 4px;
          background: ${badgeColor};
          color: white;
        ">
          <span style="
            font-size: 8px;
            font-weight: 700;
          ">#${index + 1}</span>
          ${hasWarning ? '<span style="font-size: 8px;">⚠️</span>' : ''}
        </div>
        
        <!-- Content -->
        <div style="padding: 2px 4px;">
          <div style="
            font-size: 7px;
            font-weight: 600;
            color: #374151;
            line-height: 1.2;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          ">${truncatedSubject}</div>
        </div>
      </div>
      
      <!-- Arrow pointer -->
      <div style="
        width: 0;
        height: 0;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        border-top: 4px solid ${borderColor};
        margin: 0 auto;
        margin-top: -1px;
      "></div>
    `

    // Build info window content
    let warningHtml = ''
    if (ticket.warning) {
      warningHtml = `
        <div class="bg-red-50 border border-red-200 rounded p-2 mt-2">
          <p class="text-xs text-red-700 font-medium">⚠️ ${ticket.warning.message}</p>
          <button 
            type="button"
            onclick="window.dispatchEvent(new CustomEvent('edit-ticket-address', { detail: { ticketId: ${ticket.id} } }))"
            class="mt-1 text-xs bg-red-100 text-red-800 px-2 py-1 rounded hover:bg-red-200">
            ✏️ Editar dirección
          </button>
        </div>
      `
    }

    let infoHtml = ''
    if (ticket.info) {
      infoHtml = `
        <div class="bg-blue-50 border border-blue-200 rounded p-2 mt-2">
          <p class="text-xs text-blue-700">ℹ️ ${ticket.info.message}</p>
        </div>
      `
    }

    // Show address type indicator
    let addressTypeIcon = ''
    switch (ticket.address_type) {
      case 'google_maps_url':
        addressTypeIcon = '🔗'
        break
      case 'coordinates':
        addressTypeIcon = '📍'
        break
      default:
        addressTypeIcon = '🏠'
    }

    const infoWindow = new google.maps.InfoWindow({
      content: `
        <div class="p-2 max-w-xs">
          <h3 class="font-bold text-sm mb-1">#${index + 1} - ${ticket.subject}</h3>
          <p class="text-xs text-gray-600 mb-1">${addressTypeIcon} ${ticket.direccion}</p>
          ${ticket.direccion_formateada ? `<p class="text-xs text-gray-500 italic mb-1">→ ${ticket.direccion_formateada}</p>` : ''}
          <p class="text-xs"><strong>Cliente:</strong> ${ticket.nombre || 'N/A'}</p>
          ${ticket.telefono ? `<p class="text-xs"><strong>Tel:</strong> <a href="tel:${ticket.telefono}" class="text-blue-600">${ticket.telefono}</a></p>` : ''}
          ${warningHtml}
          ${infoHtml}
          <div class="mt-2 pt-2 border-t border-gray-200 flex flex-col gap-2">
            <button type="button" 
                    class="w-full text-xs bg-gray-100 text-gray-700 px-3 py-1.5 rounded hover:bg-gray-200 transition-colors"
                    onclick="window.dispatchEvent(new CustomEvent('view-ticket-from-map', { detail: { ticketId: ${ticket.id} } }))">
              👁️ Ver ticket
            </button>
            <button type="button" 
                    class="w-full text-xs bg-green-100 text-green-700 px-3 py-1.5 rounded hover:bg-green-200 transition-colors"
                    onclick="window.dispatchEvent(new CustomEvent('resolve-ticket-from-map', { detail: { ticketId: ${ticket.id} } }))">
              ✓ Resolver
            </button>
            <a href="https://www.google.com/maps/search/?api=1&query=${ticket.coordinates.lat},${ticket.coordinates.lng}" 
               target="_blank"
               class="text-xs text-blue-600 hover:underline text-center">
              🗺️ Ver en Google Maps
            </a>
          </div>
        </div>
      `
    })

    // Create the AdvancedMarkerElement
    const marker = new AdvancedMarkerElement({
      position: ticket.coordinates,
      map: this.map,
      title: ticket.subject,
      content: markerContent
    })

    // Add click listener for InfoWindow
    marker.addListener('click', () => {
      // Close any currently open InfoWindow
      if (this.currentInfoWindow) {
        this.currentInfoWindow.close()
      }
      infoWindow.open(this.map, marker)
      this.currentInfoWindow = infoWindow
    })

    this.markers.push(marker)
  }

  /**
   * Format pipeline stage name for display
   */
  formatPipelineName(stage) {
    const names = {
      'programado': 'Programado',
      'en_reparacion': 'En reparación',
      'pendiente_respuesta': 'Pendiente',
      'resuelto': 'Resuelto'
    }
    return names[stage] || stage || 'Pendiente'
  }

  /**
   * Draw route polyline on map
   */
  drawRoute(data) {
    const path = google.maps.geometry.encoding.decodePath(data.polyline)

    this.routePath = new google.maps.Polyline({
      path: path,
      geodesic: true,
      strokeColor: '#F97316',
      strokeOpacity: 0.8,
      strokeWeight: 4
    })
    this.routePath.setMap(this.map)

    // Fit bounds to show entire route
    const bounds = new google.maps.LatLngBounds()
    bounds.extend(data.origin)
    path.forEach(point => bounds.extend(point))
    this.map.fitBounds(bounds)
  }

  /**
   * Show route information overlay
   */
  showRouteInfo(data) {
    if (this.hasRouteInfoTarget) {
      let warningsHtml = ''

      // Show warnings if any
      if (data.warnings && data.warnings.length > 0) {
        warningsHtml = `
          <div class="mt-3 pt-3 border-t border-orange-200">
            <p class="text-xs font-semibold text-orange-700 mb-2">⚠️ Advertencias (${data.warnings.length})</p>
            ${data.warnings.map(w => `
              <div class="text-xs bg-orange-50 p-2 rounded mb-1">
                <span class="font-medium">${w.ticket_subject || 'Ticket #' + w.ticket_id}</span>
                <p class="text-orange-600">${w.message}</p>
              </div>
            `).join('')}
          </div>
        `
      }

      this.routeInfoTarget.innerHTML = `
        <button type="button" 
                class="absolute top-1 right-1 text-gray-400 hover:text-gray-600 p-1"
                data-action="click->bike-hunter-tabs#toggleRouteInfo"
                title="Ocultar panel">
          ✕
        </button>
        <h3 class="font-bold text-sm mb-2">🚲 Ruta en Bicicleta</h3>
        <p class="text-xs text-gray-600 mb-1">
          <strong>Distancia:</strong> ${(data.distance_meters / 1000).toFixed(1)} km
        </p>
        <p class="text-xs text-gray-600 mb-1">
          <strong>Duración:</strong> ${Math.round(data.duration_seconds / 60)} min
        </p>
        <p class="text-xs text-gray-600 mb-1">
          <strong>Paradas:</strong> ${data.tickets.length}
        </p>
        <p class="text-xs text-gray-600 mb-2">
          <strong>Ciudad:</strong> ${data.expected_city || 'No especificada'}
        </p>
        <a href="${this.buildGoogleMapsUrl(data)}" 
           target="_blank" 
           class="block text-center text-xs bg-blue-500 text-white px-3 py-2 rounded hover:bg-blue-600 mt-2">
          📱 Abrir en Google Maps
        </a>
        ${warningsHtml}
      `

      // Store warning count for the toggle button
      this.warningCount = data.warnings ? data.warnings.length : 0

      // Keep panel hidden by default, show toggle button
      this.routeInfoTarget.classList.add("hidden")
      this.updateRouteInfoToggleButton(true)
    }
  }

  /**
   * Toggle route info panel visibility
   */
  toggleRouteInfo() {
    const isHidden = this.routeInfoTarget.classList.toggle("hidden")
    this.updateRouteInfoToggleButton(isHidden)
  }

  /**
   * Update the floating toggle button visibility
   */
  updateRouteInfoToggleButton(showButton) {
    // Find or create the toggle button
    let toggleBtn = this.element.querySelector('[data-route-info-toggle]')

    if (showButton) {
      if (!toggleBtn) {
        toggleBtn = document.createElement('button')
        toggleBtn.type = 'button'
        toggleBtn.dataset.routeInfoToggle = 'true'
        toggleBtn.style.cssText = `
          background: none center / 24px no-repeat rgb(255, 255, 255);
          display: flex;
          align-items: center;
          justify-content: center;
          border: 0;
          margin: 0;
          padding: 0;
          position: absolute;
          bottom: 24px;
          left: 10px;
          cursor: pointer;
          user-select: none;
          width: 40px;
          height: 40px;
          border-radius: 2px;
          box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
          z-index: 10;
        `
        toggleBtn.title = 'Mostrar info de ruta'
        toggleBtn.dataset.action = 'click->bike-hunter-tabs#toggleRouteInfo'

        // Insert after mapContainer
        if (this.hasMapContainerTarget) {
          this.mapContainerTarget.parentElement.appendChild(toggleBtn)
        }
      }

      // Update button content with info icon and optional warning badge
      const warningBadge = this.warningCount > 0
        ? `<span style="position: absolute; top: -4px; right: -4px; background: #EF4444; color: white; font-size: 10px; font-weight: bold; min-width: 16px; height: 16px; border-radius: 8px; display: flex; align-items: center; justify-content: center; padding: 0 4px;">${this.warningCount}</span>`
        : ''

      toggleBtn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
        ${warningBadge}
      `

      toggleBtn.classList.remove('hidden')
    } else if (toggleBtn) {
      toggleBtn.classList.add('hidden')
    }
  }

  /**
   * Build Google Maps URL for navigation
   */
  buildGoogleMapsUrl(data) {
    const origin = `${data.origin.lat},${data.origin.lng}`
    const waypoints = data.tickets.map(t => `${t.coordinates.lat},${t.coordinates.lng}`).join('|')
    const destination = origin // Round trip

    // Use bicycling mode
    return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&waypoints=${waypoints}&travelmode=bicycling`
  }

  /**
   * Show error message in map container
   */
  showMapError(message) {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add("hidden")
    }

    if (this.hasRutaPanelTarget) {
      this.rutaPanelTarget.innerHTML = `
        <div class="w-full h-[600px] flex items-center justify-center bg-gray-100 rounded-lg">
          <div class="text-center p-6 bg-red-50 rounded-lg">
            <p class="text-red-600 font-semibold mb-2">❌ Error al cargar ruta</p>
            <p class="text-red-500 text-sm">${message}</p>
            <button 
              type="button"
              class="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200"
              data-action="click->bike-hunter-tabs#retryLoadMap"
            >
            </button>
          </div>
        </div>
      `
    }
  }

  /**
   * Update the warnings panel with data
   */
  updateWarningsPanel(data) {
    if (!this.hasWarningsListTarget) return

    let warnings = [...(data.warnings || [])]

    // Include general error if present (e.g. route optimization failed)
    if (!data.success && data.error) {
      warnings.unshift({
        type: 'general_error',
        message: data.error,
        ticket_id: null,
        ticket_subject: 'Error General'
      })
    }
    const count = warnings.length

    // Update badge
    if (this.hasWarningCountBadgeTarget) {
      this.warningCountBadgeTarget.textContent = count
      if (count > 0) {
        this.warningCountBadgeTarget.classList.remove('hidden')
      } else {
        this.warningCountBadgeTarget.classList.add('hidden')
      }
    }

    if (count === 0) {
      this.warningsListTarget.innerHTML = `
        <div class="flex flex-col items-center justify-center py-10 text-gray-500">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-green-500 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p class="text-lg font-medium">¡Todo en orden!</p>
          <p class="text-sm">No hay advertencias ni errores en las direcciones.</p>
        </div>
      `
      return
    }

    // Render warnings
    this.warningsListTarget.innerHTML = warnings.map(w => {
      let actionButton = ''

      // Determine field to edit
      const fieldToEdit = w.field || (w.type === 'geocode_failed' || w.type === 'city_mismatch' ? 'direccion_reparacion' : null)

      // If we can identify a field to edit, show the button
      if (fieldToEdit) {
        const buttonText = fieldToEdit === 'direccion_reparacion' ? 'Editar dirección' : 'Corregir campo'

        actionButton = `
          <button 
            type="button"
            onclick="window.dispatchEvent(new CustomEvent('edit-ticket-address', { detail: { ticketId: ${w.ticket_id}, field: '${fieldToEdit}' } }))"
            class="mt-3 inline-flex items-center px-4 py-2 border border-blue-600 text-xs font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors">
            ${buttonText}
          </button>
        `
      }

      return `
        <div class="bg-white border border-gray-200 rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow">
          <div class="flex items-start space-x-3">
            <div class="flex-shrink-0 mt-0.5">
              <div class="bg-red-100 rounded-full p-1">
                <svg class="h-4 w-4 text-red-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                </svg>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-sm font-semibold text-gray-900">
                ${w.ticket_subject || 'Ticket #' + w.ticket_id}
              </h3>
              <div class="mt-1 text-sm text-gray-600">
                <p class="text-red-600 font-medium">${w.message}</p>
                ${w.direccion_original ? `<p class="mt-1 text-xs text-gray-500 bg-gray-50 p-2 rounded border border-gray-100 font-mono break-all">${w.direccion_original}</p>` : ''}
              </div>
              ${actionButton}
            </div>
          </div>
        </div>
      `
    }).join('')
  }


  /**
   * Retry loading map after error
   */
  retryLoadMap() {
    this.cachedRouteData = null
    this.mapLoadedValue = false

    // Restore the original ruta panel HTML
    if (this.hasRutaPanelTarget) {
      this.rutaPanelTarget.innerHTML = `
        <div class="w-full h-[600px] bg-gray-100 rounded-lg overflow-hidden relative">
          <div data-bike-hunter-tabs-target="loadingIndicator" class="absolute inset-0 flex items-center justify-center">
            <div class="text-center">
              <svg class="animate-spin h-10 w-10 text-orange-500 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p class="text-gray-600">Calculando ruta óptima...</p>
            </div>
          </div>
          <div data-bike-hunter-tabs-target="mapContainer" class="w-full h-full hidden"></div>
          <div data-bike-hunter-tabs-target="routeInfo" class="absolute top-4 left-4 bg-white p-4 rounded-lg shadow-lg z-10 hidden"></div>
        </div>
      `
    }

    this.loadMap()
  }

  /**
   * Force refresh route data (can be called externally)
   */
  refreshRouteData() {
    this.cachedRouteData = null
    if (this.activeTabValue === "ruta") {
      this.loadMap()
    }
  }
}
